import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getCreditDisplay } from '../../../services/testAccountService';
import { createBillingPortalSession } from '../../../services/stripeCheckoutService';
import UpgradePlanModal from './UpgradePlanModal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

const SubscriptionPanel = ({ subscription, userId }) => {
  const { userProfile } = useAuth();
  const [creditDisplay, setCreditDisplay] = React.useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  React.useEffect(() => {
    if (userId) loadCreditDisplay();
  }, [userId]);

  const loadCreditDisplay = async () => {
    const result = await getCreditDisplay(userId);
    if (result?.success) setCreditDisplay(result);
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const result = await createBillingPortalSession(userId);
      if (!result?.success) throw new Error(result?.error);
      window.location.href = result.url;
    } catch (err) {
      toast.error(err.message || 'Failed to open billing portal');
    } finally {
      setBillingLoading(false);
    }
  };

  const planConfig = {
    free:       { name: 'Free Trial',   color: 'text-muted-foreground', bgColor: 'bg-muted',        icon: 'Gift'     },
    free_trial: { name: 'Free Trial',   color: 'text-muted-foreground', bgColor: 'bg-muted',        icon: 'Gift'     },
    single:     { name: 'Single Case',  color: 'text-primary',          bgColor: 'bg-primary/10',   icon: 'FileText' },
    monthly:    { name: 'Monthly Plan', color: 'text-success',          bgColor: 'bg-success/10',   icon: 'Calendar' },
    annual:     { name: 'Annual Plan',  color: 'text-primary',          bgColor: 'bg-primary/10',   icon: 'Award'    },
    enterprise: { name: 'Enterprise',   color: 'text-warning',          bgColor: 'bg-warning/10',   icon: 'Building' },
  };

  const config = planConfig?.[subscription?.planType] || planConfig?.free;
  const isFreeTier = !subscription?.planType || subscription?.planType === 'free' || subscription?.planType === 'free_trial';

  return (
    <>
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Subscription</h2>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${config?.bgColor}`}>
          <Icon name={config?.icon} size={16} className={config?.color} />
          <span className={`caption font-medium ${config?.color}`}>{config?.name}</span>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="CreditCard" size={20} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Credits Remaining</span>
            {creditDisplay?.isTestAccount && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                TEST MODE
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-foreground">
              {creditDisplay?.displayValue || creditDisplay?.credits || 0}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={20} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Billing Period</span>
          </div>
          <span className="text-sm text-muted-foreground">{subscription?.billingPeriod}</span>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Monthly Cost</span>
          </div>
          <span className="text-lg font-semibold text-foreground">${subscription?.monthlyCost}</span>
        </div>

        {subscription?.nextBillingDate && (
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={20} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Next Billing</span>
            </div>
            <span className="text-sm text-muted-foreground">{subscription?.nextBillingDate}</span>
          </div>
        )}
      </div>
      <div className="space-y-3">
        <Button
          variant="default"
          iconName="TrendingUp"
          iconPosition="left"
          fullWidth
          onClick={() => setShowUpgradeModal(true)}
        >
          Upgrade Plan
        </Button>
        <Button
          variant="outline"
          iconName={billingLoading ? 'Loader2' : 'Settings'}
          iconPosition="left"
          fullWidth
          disabled={billingLoading}
          onClick={handleManageBilling}
        >
          {billingLoading ? 'Opening portal…' : 'Manage Billing'}
        </Button>
      </div>
      {isFreeTier && (
        <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <p className="text-sm text-foreground">
              You have <span className="font-semibold">1 free report</span> available with verified email and card on file.
            </p>
          </div>
        </div>
      )}
    </div>

    <UpgradePlanModal
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      userId={userId}
      userEmail={userProfile?.email}
      currentPlan={subscription?.planType}
      hasActiveSub={!!subscription?.isActive}
    />
    </>
  );
};

export default SubscriptionPanel;